import styled from '@emotion/styled';
import { Spacings, mediaQuery } from '@styles/index';
import { Typography } from 'aquilla/core/atoms/Typography';
import React from 'react';
import useProfile from '@api/hooks/profile/useProfile';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import { getYear } from '@utils/dates';
import EducatorHighlights from './EducatorHighLights';

const Container = styled.div`
  margin-bottom: -${Spacings.SPACING_10B};
  border-bottom: 1px solid var(--color-divider);
`;

const LeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 264px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-top: 0;
  }
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_8B};
`;

const InnerContainer = styled.div`
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: ${Spacings.SPACING_8B} auto ${Spacings.SPACING_10B};
  padding-bottom: ${Spacings.SPACING_6B};
  display: flex;
  flex-direction: row;
  gap: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B} 0;
    flex-direction: column;
    margin-top: 0;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};

  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_3B};
    padding: ${({ $area }) =>
        $area === 'bio' ? Spacings.SPACING_8B : Spacings.SPACING_3B}
      ${Spacings.SPACING_4B} ${Spacings.SPACING_3B};
    background-color: var(--color-base-1);
  }
`;

const SectionHeader = ({ children }) => (
  <Typography variant="h4" color="var(--color-text-primary)">
    {children}
  </Typography>
);

const SectionDetails = styled.div``;
const WorkHistory = styled.div``;
const AccomplishmentPoints = styled.div`
  display: flex;
  gap: 8px;
  &::before {
    content: '•';
    color: var(--color-text-primary);
  }
`;

const getIntervalText = (highlight) => {
  return `•${Spacings.SPACING_2SPACES}${getYear(highlight.startTime)} - ${
    highlight.currentlyRelated ? 'Present' : getYear(highlight.endTime)
  }`;
};

const EducatorAbout = ({ username }) => {
  const selectedGoalUID = useSelectedGoalUID();
  const { profileData: educator = {} } = useProfile(username, {
    goalUid: selectedGoalUID
  });
  const { bio, firstName, lastName, credentials } = educator;
  const workHistory = credentials?.find((item) => item.type === 1)?.value;
  const accomplishments = credentials?.find((item) => item.type === 4)?.value;
  let name = firstName;
  if (lastName) name += ` ${lastName}`;
  return (
    <Container>
      <InnerContainer>
        <LeftContainer>
          <EducatorHighlights educator={educator} username={username} />
        </LeftContainer>
        <RightContainer>
          {bio && (
            <Section $area="bio">
              <SectionHeader>{name}&apos;s bio</SectionHeader>
              <Typography variant="p2" color="var(--color-text-primary)">
                {bio}
              </Typography>
            </Section>
          )}
          {workHistory && workHistory.length > 0 && (
            <Section $area="work">
              <SectionHeader>Work</SectionHeader>
              <SectionDetails>
                {workHistory.map((work, index) => {
                  return (
                    <WorkHistory
                      key={
                        work.startTime ? `${work.startTime}_${index}` : index
                      }
                    >
                      <Typography
                        variant="h6"
                        color="var(--color-text-primary)"
                      >
                        {getIntervalText(work)}
                      </Typography>
                      <Typography
                        variant="p2"
                        color="var(--color-text-primary)"
                      >
                        {work.title}
                      </Typography>
                    </WorkHistory>
                  );
                })}
              </SectionDetails>
            </Section>
          )}
          {accomplishments && accomplishments.length > 0 && (
            <Section $area="accomplishment">
              <SectionHeader>Accomplishment</SectionHeader>
              <SectionDetails>
                {accomplishments.map((accomplishment, index) => {
                  return (
                    <AccomplishmentPoints key={index.toString()}>
                      <Typography
                        variant="p2"
                        color="var(--color-text-primary)"
                      >
                        {`${accomplishment.title}`}
                      </Typography>
                    </AccomplishmentPoints>
                  );
                })}
              </SectionDetails>
            </Section>
          )}
        </RightContainer>
      </InnerContainer>
    </Container>
  );
};

export default EducatorAbout;
